import {API} from "api";
import {DEFAULT_PRODUCTS_MAPPING, DEFAULT_TYPES} from "data/constants";
import {CHANNELS, PRODUCTS, productCodesForSelector} from "data/products";
import {releasesSetsForProduct} from "data/products/index";
import {homeChannel, homeType} from "data/offering";
import {getChannelSettingsQueryKey, type ChannelSettings} from "hooks/useChannelSettings";
import type {Viewer} from "libs/lastLocation";
import {queryClient} from "libs/queryClient";
import {pageLayoutOffer} from "libs/layoutOffer";
import {offeredSelectableStages, productsNotOffered} from "libs/productOffer";
import {hiddenSetsOf, landingProduct, landingSetParam} from "libs/selectorVisibility";
import {resolveDefaultStage} from "libs/stageOrder";
import {getVisibleChannels} from "utils/roles";
import {DEFAULT_PARAMS, buildUrl} from "utils/url";

// ---------------------------------------------------------------------------
// Where a channel opens when the caller names no content type — the index
// redirect, the bare /<channel> routes, and the toolbar's channel switch.
//
// The type used to come straight from the declared DEFAULT_TYPES table, which
// made the admin-saved Stage order a display-only setting: an admin could put
// EAP at the top and every user still landed on Release. The saved order now
// decides the landing stage as well, which is what "the first option leads"
// has always promised.
//
// The landing also skips what an admin hid from the selectors: a hidden
// default product gives way to the first visible one, a stage hidden for the
// product to its first offered stage, and a hidden first set to the first
// visible set of that stage (libs/selectorVisibility.ts). A link that names a
// hidden product, stage or set still opens it; only this choice changes.
//
// The layout is the default layout of the page: the first offered layout of
// the admin's layout order, else layout 1 when it is offered.
// ---------------------------------------------------------------------------

/** The channel's saved settings, or null when there are none to apply. */
async function loadChannelSettings(channelValue: string): Promise<ChannelSettings | null> {
  try {
    return await queryClient.ensureQueryData<ChannelSettings>({
      queryKey: getChannelSettingsQueryKey(channelValue),
      queryFn: () => API.fetchChannelSettings(channelValue),
      staleTime: 5 * 60 * 1000,
    });
  } catch {
    // Same contract as useChannelSettings: a backend that predates the
    // endpoint 404s and the declared order is then the right answer. This one
    // sits in front of a redirect, so a network blip must not strand the user
    // on a blank route either.
    return null;
  }
}

/** The channel's products in the Product dropdown's order (by label). */
function productCodesByLabel(channelValue: string, typeValue: string): string[] {
  const offered = new Set<string>(productCodesForSelector(channelValue, typeValue));
  return (PRODUCTS as Array<{value: string}>)
    .filter((product) => offered.has(product.value))
    .map((product) => product.value);
}

/**
 * The path a channel opens on, e.g. `releases/IJ/eap?layout=1&background=1`.
 * Callers prefix the leading slash themselves, the way buildUrl's callers do.
 */
export async function resolveChannelLandingPath(channelValue: string): Promise<string> {
  // data/constants.js is still JavaScript, so its literal maps carry no index
  // signature. The lookups below are by definition open-ended — the channel
  // comes from a URL — and both handle a miss.
  const productsByChannel = DEFAULT_PRODUCTS_MAPPING as Record<string, string | undefined>;
  const typesByChannel = DEFAULT_TYPES as Record<string, {value: string} | undefined>;

  const declaredDefault = typesByChannel[channelValue]?.value;
  // Nothing to reorder for a channel with no declared default — buildUrl owns
  // that error, and it must keep owning it.
  if (!declaredDefault) return buildUrl(channelValue);

  const settings = await loadChannelSettings(channelValue);
  // null lets buildUrl apply its own product default, exactly as before; the
  // stage lookup then finds no offered types and keeps the declared default.
  const declaredProduct = productsByChannel[channelValue] ?? null;
  const candidates = productCodesByLabel(channelValue, declaredDefault);
  const productValue = landingProduct(
    declaredProduct,
    candidates,
    productsNotOffered(
      settings,
      channelValue,
      declaredProduct ? [declaredProduct, ...candidates] : candidates,
      declaredDefault
    )
  );
  const typeValue = resolveDefaultStage(
    settings?.stageOrder ?? null,
    offeredSelectableStages(settings, channelValue, productValue),
    declaredDefault
  );

  const params: Record<string, string | number> = {...DEFAULT_PARAMS};
  if (!productValue) return buildUrl(channelValue, productValue, typeValue, params);

  // The page's data: sets belong to the releases offering, and so do their
  // hidden lists and the layout rules of a set, whichever channel lists the
  // product (data/offering.ts).
  const dataChannel = homeChannel(channelValue, productValue);
  const dataType = homeType(typeValue, productValue);
  const dataSettings =
    dataChannel === channelValue ? settings : await loadChannelSettings(dataChannel);
  let set: string | undefined;
  if (dataChannel === "releases") {
    const setValues = releasesSetsForProduct(productValue).map(
      (item: {value: string}) => item.value
    );
    const setParam = landingSetParam(setValues, hiddenSetsOf(dataSettings, dataType, productValue));
    if (setParam) params.set = setParam;
    set = setParam ?? setValues[0];
  }
  params.layout = pageLayoutOffer({
    dataChannel,
    type: dataType,
    set,
    product: productValue,
    settings: dataSettings,
    declared: DEFAULT_PARAMS.layout,
  }).defaultId;
  return buildUrl(channelValue, productValue, typeValue, params);
}

/**
 * The default landing: the first channel that the viewer can see, on the
 * landing path of that channel. The index route opens it when there is no
 * last location to open (libs/lastLocation.ts). The Home button of the
 * toolbar always opens it.
 */
export async function resolveHomeLandingPath(viewer: Viewer): Promise<string> {
  const visibleChannels = getVisibleChannels(CHANNELS, viewer.role, viewer.featureFlags);
  const target = visibleChannels[0] ?? CHANNELS[0];
  return resolveChannelLandingPath(target.value);
}
